// frontend/src/COIDeclarationModal.tsx - Conflict of Interest Declaration Modal

import React, { useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface COIDeclarationModalProps {
  processId: number;
  processName: string;
  suppliers: Array<{id: number; name: string; location?: string}>;
  onSuccess: () => void;
  onCancel: () => void;
}

interface COIFormData {
  has_conflict: boolean;
  conflict_type?: 'actual' | 'potential' | 'perceived';
  conflict_details: string;
  confidentiality_agreed: boolean;
  management_strategy: string;
}

const COIDeclarationModal: React.FC<COIDeclarationModalProps> = ({
  processId,
  processName,
  suppliers,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<COIFormData>({
    has_conflict: false,
    conflict_type: undefined,
    conflict_details: '',
    confidentiality_agreed: false,
    management_strategy: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.confidentiality_agreed) {
      setError('You must agree to maintain confidentiality to proceed.');
      return;
    }

    if (formData.has_conflict && !formData.conflict_type) {
      setError('Please select the type of conflict of interest.');
      return;
    }

    if (formData.has_conflict && !formData.conflict_details.trim()) {
      setError('Please provide details about your conflict of interest.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = Cookies.get('access_token');
      await axios.post(
        `${API_URL}/api/coi/declare`,
        {
          process_id: processId,
          has_conflict: formData.has_conflict,
          conflict_type: formData.conflict_type,
          conflict_details: formData.conflict_details,
          confidentiality_agreed: formData.confidentiality_agreed,
          management_strategy: formData.management_strategy
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit COI declaration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 'var(--spacing-md)',
      paddingTop: '5rem'
    },
    modal: {
      backgroundColor: 'var(--color-surface)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--spacing-xl)',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto' as const,
      boxShadow: 'var(--shadow-xl)',
      border: '1px solid var(--color-surface-variant)',
      fontFamily: 'var(--font-family-primary)'
    },
    header: {
      marginBottom: 'var(--spacing-lg)',
      paddingBottom: 'var(--spacing-md)',
      borderBottom: '2px solid var(--color-surface-variant)'
    },
    title: {
      fontSize: 'var(--font-size-title-large)',
      fontWeight: 'var(--font-weight-title)',
      color: 'var(--color-text-primary)',
      margin: 0,
      lineHeight: 'var(--line-height-title-large)'
    },
    subtitle: {
      fontSize: 'var(--font-size-body-medium)',
      color: 'var(--color-text-secondary)',
      marginTop: 'var(--spacing-sm)',
      lineHeight: 'var(--line-height-body-medium)'
    },
    question: {
      fontSize: 'var(--font-size-body-large)',
      fontWeight: 'var(--font-weight-label)',
      color: 'var(--color-text-primary)',
      marginBottom: 'var(--spacing-md)',
      lineHeight: 'var(--line-height-body-large)'
    },
    optionContainer: {
      marginBottom: 'var(--spacing-lg)'
    },
    option: {
      border: '2px solid var(--color-surface-variant)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--spacing-md)',
      marginBottom: 'var(--spacing-sm)',
      cursor: 'pointer',
      transition: 'all var(--transition-normal)',
      backgroundColor: 'var(--color-surface)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'var(--spacing-sm)'
    },
    optionSelected: {
      border: '2px solid var(--color-primary)',
      backgroundColor: 'var(--color-primary-50)'
    },
    optionContent: {
      flex: 1
    },
    optionTitle: {
      fontSize: 'var(--font-size-body-large)',
      fontWeight: 'var(--font-weight-label)',
      color: 'var(--color-text-primary)',
      marginBottom: 'var(--spacing-xs)'
    },
    optionDescription: {
      fontSize: 'var(--font-size-body-medium)',
      color: 'var(--color-text-secondary)',
      lineHeight: 'var(--line-height-body-medium)'
    },
    textarea: {
      width: '100%',
      padding: 'var(--spacing-sm)',
      border: '2px solid var(--color-surface-variant)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--font-size-body-medium)',
      fontFamily: 'var(--font-family-primary)',
      resize: 'vertical' as const,
      minHeight: '80px',
      marginBottom: 'var(--spacing-md)',
      boxSizing: 'border-box' as const,
      transition: 'border-color var(--transition-normal)',
      color: 'var(--color-text-primary)'
    },
    textareaFocus: {
      borderColor: 'var(--color-primary)',
      outline: 'none'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'var(--spacing-sm)',
      padding: 'var(--spacing-md)',
      backgroundColor: 'var(--color-warning-light)',
      border: '1px solid var(--color-warning)',
      borderRadius: 'var(--radius-md)',
      marginBottom: 'var(--spacing-lg)'
    },
    checkboxLabel: {
      fontSize: 'var(--font-size-body-medium)',
      color: '#92400e',
      lineHeight: 'var(--line-height-body-medium)',
      fontWeight: 'var(--font-weight-label)'
    },
    buttonContainer: {
      display: 'flex',
      gap: 'var(--spacing-md)',
      justifyContent: 'flex-end',
      marginTop: 'var(--spacing-xl)'
    },
    note: {
      marginTop: 'var(--spacing-lg)',
      padding: 'var(--spacing-md)',
      backgroundColor: 'var(--color-info-light)',
      border: '1px solid var(--color-info)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--font-size-body-small)',
      color: 'var(--color-info)',
      lineHeight: 'var(--line-height-body-small)'
    },
    errorMessage: {
      padding: 'var(--spacing-sm)',
      backgroundColor: 'var(--color-error-light)',
      border: '1px solid var(--color-error)',
      borderRadius: 'var(--radius-md)',
      color: 'var(--color-error)',
      fontSize: 'var(--font-size-body-medium)',
      marginBottom: 'var(--spacing-md)'
    },
    supplierSection: {
      marginBottom: 'var(--spacing-lg)',
      padding: 'var(--spacing-md)',
      backgroundColor: 'var(--color-surface-variant)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--color-surface-variant)'
    },
    supplierList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 'var(--spacing-sm)',
      marginTop: 'var(--spacing-sm)'
    },
    supplierItem: {
      padding: 'var(--spacing-xs)',
      backgroundColor: 'var(--color-surface)',
      borderRadius: 'var(--radius-sm)',
      fontSize: 'var(--font-size-body-small)',
      color: 'var(--color-text-primary)',
      border: '1px solid #e5e7eb'
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Conflict of Interest Assessment</h2>
          <p style={styles.subtitle}>Process: {processName}</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          <div style={styles.supplierSection}>
            <div style={styles.question}>
              Suppliers participating in this procurement:
            </div>
            <div style={styles.supplierList}>
              {suppliers.map(supplier => (
                <div key={supplier.id} style={styles.supplierItem}>
                  <strong>{supplier.name}</strong>
                  {supplier.location && <div>{supplier.location}</div>}
                </div>
              ))}
            </div>
          </div>

          <div style={styles.question}>
            Do you have any financial, professional, or personal interests that could
            reasonably be perceived to influence your evaluation of this procurement?
          </div>

          <div style={styles.optionContainer}>
            <div
              style={{
                ...styles.option,
                ...(formData.has_conflict === false ? styles.optionSelected : {})
              }}
              onClick={() => setFormData(prev => ({ ...prev, has_conflict: false }))}
            >
              <input
                type="radio"
                name="conflict"
                checked={formData.has_conflict === false}
                onChange={() => {}}
                style={{ marginTop: '2px' }}
              />
              <div style={styles.optionContent}>
                <div style={styles.optionTitle}>No Conflict</div>
                <div style={styles.optionDescription}>
                  I have no conflicts of interest and can evaluate this procurement impartially.
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.option,
                ...(formData.has_conflict === true ? styles.optionSelected : {})
              }}
              onClick={() => setFormData(prev => ({ ...prev, has_conflict: true, conflict_type: undefined }))}
            >
              <input
                type="radio"
                name="conflict"
                checked={formData.has_conflict === true}
                onChange={() => {}}
                style={{ marginTop: '2px' }}
              />
              <div style={styles.optionContent}>
                <div style={styles.optionTitle}>Yes, I Have a Conflict</div>
                <div style={styles.optionDescription}>
                  I have a conflict of interest that requires disclosure and management.
                </div>
              </div>
            </div>
          </div>

          {formData.has_conflict && (
            <div>
              <div style={styles.question}>
                What type of conflict of interest do you have?
              </div>

              <div style={styles.optionContainer}>
                <div
                  style={{
                    ...styles.option,
                    ...(formData.conflict_type === 'actual' ? styles.optionSelected : {})
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, conflict_type: 'actual' }))}
                >
                  <input
                    type="radio"
                    name="conflict_type"
                    checked={formData.conflict_type === 'actual'}
                    onChange={() => {}}
                    style={{ marginTop: '2px' }}
                  />
                  <div style={styles.optionContent}>
                    <div style={styles.optionTitle}>Actual Conflict</div>
                    <div style={styles.optionDescription}>
                      I have a direct financial or personal interest that will be affected by the outcome.
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.option,
                    ...(formData.conflict_type === 'potential' ? styles.optionSelected : {})
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, conflict_type: 'potential' }))}
                >
                  <input
                    type="radio"
                    name="conflict_type"
                    checked={formData.conflict_type === 'potential'}
                    onChange={() => {}}
                    style={{ marginTop: '2px' }}
                  />
                  <div style={styles.optionContent}>
                    <div style={styles.optionTitle}>Potential Conflict</div>
                    <div style={styles.optionDescription}>
                      A conflict of interest may arise during the evaluation process.
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.option,
                    ...(formData.conflict_type === 'perceived' ? styles.optionSelected : {})
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, conflict_type: 'perceived' }))}
                >
                  <input
                    type="radio"
                    name="conflict_type"
                    checked={formData.conflict_type === 'perceived'}
                    onChange={() => {}}
                    style={{ marginTop: '2px' }}
                  />
                  <div style={styles.optionContent}>
                    <div style={styles.optionTitle}>Perceived Conflict</div>
                    <div style={styles.optionDescription}>
                      Others might reasonably perceive a conflict, even if none actually exists.
                    </div>
                  </div>
                </div>
              </div>

              <label style={styles.question}>
                Please describe your conflict of interest:
              </label>
              <textarea
                style={styles.textarea}
                value={formData.conflict_details}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  conflict_details: e.target.value
                }))}
                placeholder="Please provide details about your conflict of interest..."
                required
              />

              <label style={styles.question}>
                How do you propose to manage this conflict?
              </label>
              <textarea
                style={styles.textarea}
                value={formData.management_strategy}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  management_strategy: e.target.value
                }))}
                placeholder="Please describe how you plan to manage or mitigate this conflict..."
              />
            </div>
          )}

          <div style={styles.checkbox}>
            <input
              type="checkbox"
              id="confidentiality"
              checked={formData.confidentiality_agreed}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                confidentiality_agreed: e.target.checked
              }))}
              required
            />
            <label htmlFor="confidentiality" style={styles.checkboxLabel}>
              I understand and agree to maintain strict confidentiality regarding all
              procurement information, evaluation materials, and deliberations. I will
              not disclose any information to unauthorised parties.
            </label>
          </div>

          <div style={styles.note}>
            <strong>Note:</strong> This declaration will be reviewed by the Process Owner. 
            You cannot begin evaluation until your declaration is approved. All declarations 
            are recorded for audit purposes.
          </div>

          <div style={styles.buttonContainer}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || !formData.confidentiality_agreed}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Declaration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default COIDeclarationModal;